import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Snapshot } from '../worklog/types';

// /api/work/snapshot response wrapper from functions/api/work/snapshot.js
interface StatusResponse {
  snapshot: Snapshot;
  fetched_at: number;
  cache?: 'hit' | 'miss';
}

interface StatusContextValue {
  snapshot: Snapshot | null;
  lastSuccessfulFetchAt: number;   // epoch seconds
  secondsSinceFetch: number;
  isConnected: boolean;
  refresh: () => Promise<void>;
  // Truth-telling surface. The cached snapshot is a convenience, never a
  // substitute for live data: whenever it is standing in for a failed poll
  // the UI must say so rather than render month-old numbers as current.
  isStale: boolean;
  fromCache: boolean;
  lastError: string | null;
  hasEverLoaded: boolean;
}

const LS_KEY = 'worklog::work::lastSnapshot';
const POLL_INTERVAL_MS = 3_000;        // 3 seconds — direct D1 reads are fast
const TICK_INTERVAL_MS = 1_000;
const CONNECTION_BANNER_AFTER_S = 60;  // 60s of failed polls = real outage
const SNAPSHOT_ENDPOINT = '/api/work/snapshot';
// Past this age the cached snapshot is not "slightly behind", it is a
// different week. Anything older is rendered as stale, never as current.
const STALE_AFTER_S = 30;

const Ctx = createContext<StatusContextValue | null>(null);

// The cache exists to avoid a blank frame on reload, nothing more. It is
// bounded on both age and schema: an unbounded cache is what let a July-5
// snapshot render as current for a month. Anything past CACHE_MAX_AGE_S is
// discarded outright rather than shown, because a snapshot that old cannot
// be made honest by labelling.
const CACHE_VERSION = 2;
const CACHE_MAX_AGE_S = 15 * 60;

function loadCachedSnapshot(): { snapshot: Snapshot | null; ts: number } {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { snapshot: null, ts: 0 };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.snapshot) return { snapshot: null, ts: 0 };
    // Written by a previous schema: drop it rather than guess its shape.
    if (parsed.v !== CACHE_VERSION) {
      localStorage.removeItem(LS_KEY);
      return { snapshot: null, ts: 0 };
    }
    const ts = parsed.ts || 0;
    if (Math.floor(Date.now() / 1000) - ts > CACHE_MAX_AGE_S) {
      localStorage.removeItem(LS_KEY);
      return { snapshot: null, ts: 0 };
    }
    return { snapshot: parsed.snapshot, ts };
  } catch {}
  return { snapshot: null, ts: 0 };
}

function saveCachedSnapshot(snapshot: Snapshot, ts: number) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ v: CACHE_VERSION, snapshot, ts }));
  } catch {}
}

export function StatusProvider({ children }: { children: React.ReactNode }) {
  const cached = useRef(loadCachedSnapshot());
  const [snapshot, setSnapshot] = useState<Snapshot | null>(cached.current.snapshot);
  const [lastSuccessfulFetchAt, setLastSuccessfulFetchAt] = useState<number>(cached.current.ts);
  const [lastError, setLastError] = useState<string | null>(null);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const [, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const resp = await fetch(SNAPSHOT_ENDPOINT, { credentials: 'same-origin' });
      if (!resp.ok) {
        // 404 here is the owner gate refusing us (functions/api/work/snapshot.js
        // returns 404 {error:'forbidden'} to a non-owner), not a missing route.
        // Naming it is the difference between "log back in" and a month of
        // silently reading a stale cache.
        throw new Error(
          resp.status === 404 || resp.status === 403
            ? 'not authorized (HTTP ' + resp.status + ') - owner session expired; re-auth via /auth?key=...'
            : 'HTTP ' + resp.status
        );
      }
      const json: StatusResponse = await resp.json();
      if (!json.snapshot) throw new Error('no snapshot in response');
      const ts = Math.floor(Date.now() / 1000);
      setSnapshot(json.snapshot);
      setLastSuccessfulFetchAt(ts);
      setLastError(null);
      setHasEverLoaded(true);
      saveCachedSnapshot(json.snapshot, ts);
    } catch (err) {
      // Never silent. The previous snapshot is retained only so the layout
      // does not collapse; isStale/lastError force the UI to label it. A
      // failed poll must be visible within seconds, not never.
      setLastError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  // Initial fetch + scheduled polls
  useEffect(() => {
    void refresh();
    const id = setInterval(() => { void refresh(); }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // 1s wall-clock tick so secondsSinceFetch + live-counter stay current
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const nowS = Math.floor(Date.now() / 1000);
  const secondsSinceFetch = lastSuccessfulFetchAt > 0
    ? Math.max(0, nowS - lastSuccessfulFetchAt)
    : Number.POSITIVE_INFINITY;
  const isConnected = secondsSinceFetch <= CONNECTION_BANNER_AFTER_S;
  const isStale = secondsSinceFetch > STALE_AFTER_S;
  // True while we are showing something we have not confirmed this session.
  const fromCache = !hasEverLoaded && snapshot !== null;

  return (
    <Ctx.Provider value={{
      snapshot, lastSuccessfulFetchAt, secondsSinceFetch, isConnected, refresh,
      isStale, fromCache, lastError, hasEverLoaded,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useStatus(): StatusContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStatus must be inside <StatusProvider>');
  return v;
}
